import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

import { useAccessPermissions } from "./useAccessPermissions";
import { useOrganizationCapabilities } from "./useCapabilities";
import { useEffectiveOrgRole } from "./useEffectiveOrgRole";
import UnauthorizedAccess from "./UnauthorizedAccess";

// Owner-only nested gate (e.g. admin/:orgId/settings). Stricter than
// RequireOrgRole: requires organization:id:update on this org specifically —
// an edit permission, deliberately NOT the organization:id:* wildcard
// (Leadership's role holds organization:id:read but not :update, and must
// still be denied this Owner-only settings page). Permission-only, no raw
// role-name check: OrganizationOwner passes today because that role's own
// permissions[] grant organization:id:update, not because of the role name
// itself — if that permission set ever changed on the backend, this
// correctly reflects it instead of silently overriding it via role name.
// Must stay consistent with AdminNavBar's "Org Settings" link, which uses the
// exact same check. useOrganizationCapabilities is already scoped to THIS
// org's role (see useEffectiveOrgPermission): it checks the specific role
// useEffectiveOrgRole(orgId) reports for this org against THAT role's own
// permissions, not the user's global cross-org union — a per-org Owner
// assignment on a DIFFERENT org can't leak in here.
const RequireOrgOwnerRole = () => {
  const { orgId } = useParams();
  const location = useLocation();
  const authToken = useSelector((state) => state.auth?.authToken);

  const { isLoading: isLoadingPermissions } = useAccessPermissions();
  const { canUpdateOrganizationById } = useOrganizationCapabilities(orgId);
  const { isLoading: isLoadingOrgRole } = useEffectiveOrgRole(orgId);

  if (!authToken) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (isLoadingPermissions || isLoadingOrgRole) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const isAllowed = canUpdateOrganizationById;

  if (!isAllowed) {
    return (
      <UnauthorizedAccess message="Only organization owners have access to this page." />
    );
  }

  return <Outlet />;
};

export default RequireOrgOwnerRole;
